import { Fragment, memo, useMemo, useState } from 'react'
import { useFlags } from 'launchdarkly-react-client-sdk'
import LoadingState from 'components/Spinner'
import { WithAccountProps } from 'hoc/withAccount'
import useActions, {
  isCompleteFormsortIntakeFormAction,
} from 'hooks/useTimeline/useActions'
import ActionCard from './ActionCard'
import DashboardCard from './Card'
import PrescriptionCheckinModal from './Modals/PrescriptionCheckinModal'
import useAppointments from 'hooks/useAppointments/useAppointments'
import _ from 'lodash'
import { usePrescriptionCheckins } from 'hooks/usePrescriptionCheckins'
import MedicationFollowupModal from './Modals/MedicationFollowupModal'
import type {
  MedicationFeeling,
  CheckinAnswers,
} from './Modals/PrescriptionCheckinModal'
import { Amplitude } from 'utils/amplitude.utils'
import { BookingEventSource } from 'utils/amplitude.types'
import useAppointmentTypeId from 'hooks/useAppointments/useAppointmentTypeId'
import { generateAppointmentLink } from 'utils/appointments.utils'
import { useHistory } from 'react-router-dom'
import type {
  CarePlanCheckInStartEventProps,
  CarePlanCheckInDoneEventProps,
} from 'utils/amplitude.types'

const ComingUp: React.FC<WithAccountProps> = (props) => {
  const history = useHistory()
  const { isLoading, activeActions, actionDefinitions } = useActions()
  const { appointments } = useAppointments()
  const { prescriptionCheckinEnabled } = useFlags()
  const {
    isLoading: prescriptionCheckInLoading,
    incompleteCheckin,
    submitPrescriptionCheckin,
    refetch,
  } = usePrescriptionCheckins()
  const {
    appointmentTypeId: followupMdAppointmentTypeId,
  } = useAppointmentTypeId('Followup MD')

  const [maxNumberShown, setMaxNumberShown] = useState<number>(3)
  const [hiddenActions, setHiddenActions] = useState<Set<string>>(new Set())
  const [isPrescriptionModalOpen, setIsPrescriptionModalOpen] = useState(false)
  const [isSubmittingCheckin, setIsSubmittingCheckin] = useState(false)
  const [isMedicalFollowupModalOpen, setIsMedicalFollowupModalOpen] = useState(
    false
  )
  const [checkinAnswers, setCheckinAnswers] = useState<CheckinAnswers>({
    comment: '',
    medicationFeeling: '',
  })

  const handleShowMore = () => setMaxNumberShown((n) => n + 3)
  const handleShowLess = () => setMaxNumberShown((n) => n - 3)

  const actionsToShow = useMemo(() => activeActions.slice(0, maxNumberShown), [
    activeActions,
    maxNumberShown,
  ]).map((action) => ({ ...action, isHidden: hiddenActions.has(action.id) }))

  const hideAction = (actionId: string) => {
    setHiddenActions((prev) => new Set(prev).add(actionId))
  }

  const handlePrescriptionCheckinClick = () => {
    if (!incompleteCheckin?.id) {
      console.warn(
        'Cannot start prescription checkin: incompleteCheckin is null or missing id'
      )
      return
    }

    const amplitudeProps: CarePlanCheckInStartEventProps = {
      source: BookingEventSource.PRESCRIPTION_CHECKIN,
      resource_id: incompleteCheckin.id,
      checkin_type: 'prescription',
    }

    setIsPrescriptionModalOpen(true)
    Amplitude.carePlanCheckInStart(amplitudeProps)
  }

  const handlePrescriptionModalClose = () => {
    setIsPrescriptionModalOpen(false)
    setCheckinAnswers({ comment: '', medicationFeeling: '' })
  }

  const handlePrescriptionSubmit = async ({
    id,
    comment,
    medicationFeeling,
  }: {
    id: string
    comment?: string
    medicationFeeling: MedicationFeeling
  }) => {
    setIsSubmittingCheckin(true)

    try {
      await submitPrescriptionCheckin({
        id,
        medicationFeeling,
        comment,
      })

      setIsPrescriptionModalOpen(false)
      setIsMedicalFollowupModalOpen(true)
    } catch (error) {
      console.error('Error submitting prescription checkin:', error)
    } finally {
      setIsSubmittingCheckin(false)
    }
  }

  const handleBookAppointmentRedirect = () => {
    if (
      !followupMdAppointmentTypeId ||
      !incompleteCheckin?.id ||
      !checkinAnswers.medicationFeeling
    ) {
      console.warn(
        'Cannot book appointment: missing appointment type ID or checkin data or medication feeling'
      )
      return
    }

    // Store the ID in a local variable to prevent race conditions
    const checkinId = incompleteCheckin.id

    const appointmentLink = generateAppointmentLink(
      followupMdAppointmentTypeId,
      {
        source: BookingEventSource.PRESCRIPTION_CHECKIN,
        patient_action_id: null,
        patient_action_definition: null,
        resource_id: checkinId,
      }
    )

    const amplitudeProps: CarePlanCheckInDoneEventProps = {
      source: BookingEventSource.PRESCRIPTION_CHECKIN,
      resource_id: checkinId,
      feeling_scale_selected: checkinAnswers.medicationFeeling,
      free_text_note: checkinAnswers.comment || '',
      checkin_type: 'prescription',
    }

    Amplitude.carePlanCheckInDone(amplitudeProps)

    history.push(appointmentLink)
    handleFollowupModalClose()
  }

  const handleFollowupModalClose = () => {
    setIsMedicalFollowupModalOpen(false)
    setCheckinAnswers({ comment: '', medicationFeeling: '' })
    refetch()
  }

  const getResponseType = (feeling: string): 'POSITIVE' | 'CONCERNING' => {
    return ['worse', 'same'].includes(feeling) ? 'CONCERNING' : 'POSITIVE'
  }

  const getProviderName = () => {
    if (!incompleteCheckin?.provider) {
      return 'your provider'
    }

    const { firstName, lastName } = incompleteCheckin.provider

    if (!firstName && !lastName) {
      return 'your provider'
    }

    return `${firstName} ${lastName}`.trim()
  }

  return (
    <div>
      <p className="h1 py-2 fs-8">To Do</p>
      {prescriptionCheckinEnabled &&
        !prescriptionCheckInLoading &&
        incompleteCheckin && (
          <DashboardCard
            key="prescription-checkin"
            icon="https://cdn.sanity.io/images/a6l48lwi/prod/26f1dd3b97be3ab874df7bd492f41a46be626c22-34x34.svg"
            title="Checking in on your medication"
            description="It's been a few weeks since you saw your provider, so we wanted to take a moment to check-in on how your care plan is going."
            buttonText="Complete Check-in"
            displayButton={true}
            disableButton={false}
            handleClick={handlePrescriptionCheckinClick}
          />
        )}
      {!isLoading &&
        activeActions.length >= 1 &&
        actionsToShow.map((action) => {
          const originalActionDefinition = actionDefinitions.find(
            (a) => a.id === action.definition
          )

          const isActiveCompleteIntakeFormAction = isCompleteFormsortIntakeFormAction(
            action,
            originalActionDefinition,
            'active'
          )

          const actionDefinition = isActiveCompleteIntakeFormAction
            ? _.cloneDeep(originalActionDefinition)
            : originalActionDefinition

          if (isActiveCompleteIntakeFormAction && actionDefinition) {
            if (
              action.properties.formUrl.includes('initial-rd') ||
              action.properties.formUrl.includes('nutrition-only')
            ) {
              actionDefinition.title = 'Complete your nutrition intake forms'
              actionDefinition.description =
                'Please complete the following intake forms before meeting with your registered dietitian. This should only take 5 minutes.'
            } else if (action.properties.formUrl.includes('med')) {
              actionDefinition.title = 'Complete your medical intake forms'
              actionDefinition.description =
                'Please complete the following intake forms before meeting with your medical provider. This should only take 5 minutes.'
            } else {
              actionDefinition.title = 'Complete your intake forms'
              actionDefinition.description =
                'Please complete the following intake forms before your visit. This should only take about 5 minutes.'
            }

            actionDefinition.buttonText = 'Complete Forms'
            actionDefinition.buttonLink = action.properties.formUrl
          }

          if (actionDefinition && !action.isHidden) {
            return (
              <ActionCard
                key={action.id}
                action={action}
                actionDefinition={actionDefinition}
                {...props}
                appointments={appointments}
                onClickOverride={
                  isActiveCompleteIntakeFormAction
                    ? () => hideAction(action.id)
                    : undefined
                }
              />
            )
          } else {
            return <Fragment key={action.id}></Fragment>
          }
        })}
      {!isLoading && activeActions.length >= 1 && (
        <div className="d-flex justify-content-between">
          <p className="show-more-less-text" onClick={handleShowLess}>
            {maxNumberShown === 3 ? '' : 'Show Less'}
          </p>
          <p className="show-more-less-text" onClick={handleShowMore}>
            {activeActions.length > maxNumberShown ? 'Show More' : ''}
          </p>
        </div>
      )}
      {!isLoading && activeActions.length === 0 && (
        <div>
          <p className="h2 py-1 fs-4">You&apos;re all caught up!</p>
          <p>
            You&apos;ve completed all of your upcoming tasks. When you have new
            tasks, you&apos;ll see them here.
          </p>
        </div>
      )}
      {isLoading && <LoadingState />}

      {incompleteCheckin && (
        <PrescriptionCheckinModal
          isOpen={isPrescriptionModalOpen}
          onClose={handlePrescriptionModalClose}
          onSubmit={handlePrescriptionSubmit}
          isLoading={isSubmittingCheckin}
          incompleteCheckin={incompleteCheckin}
          setCheckinAnswers={setCheckinAnswers}
          checkinAnswers={checkinAnswers}
          providerName={getProviderName()}
        />
      )}
      {incompleteCheckin && (
        <MedicationFollowupModal
          isOpen={isMedicalFollowupModalOpen}
          onClose={handleFollowupModalClose}
          handleBookAppointmentRedirect={handleBookAppointmentRedirect}
          responseType={
            checkinAnswers.medicationFeeling
              ? getResponseType(checkinAnswers.medicationFeeling)
              : 'POSITIVE'
          }
          providerName={getProviderName()}
          followupMdAppointmentTypeId={followupMdAppointmentTypeId}
        />
      )}
    </div>
  )
}

export default memo(ComingUp)
